import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import ForgotPassword from './ForgotPassword';
import { trackError } from '../Utils/analytics';
import '../Auth/Auth.css';

interface LoginProps {
  onLogin: () => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Please fill in all fields');
      trackError('login_validation_failed', 'login');
      return;
    }

    setIsLoading(true);
    setError('');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const response = await fetch('https://api.homecookt.com/api/v1/users/login', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const data = await response.json().catch(() => ({})) as any;

      if (response.ok) {
        const token = data.access_token;

        if (token) {
          localStorage.setItem('authToken', token);
          localStorage.setItem('refreshToken', data.refresh_token || '');
          localStorage.setItem('tokenType', data.token_type || 'bearer');
          localStorage.setItem('userid', String(data.user_id));
          localStorage.setItem('userEmail', data.email || email);
          const roles = Array.isArray(data.role) ? data.role : (data.role ? [data.role] : []);
          localStorage.setItem('userRole', JSON.stringify(roles));
          localStorage.setItem('profileCompleted', data.profile_completed === true ? 'true' : 'false');

          onLogin();
        } else {
          setError('Login succeeded but no token was returned. Please contact support.');
          trackError('login_token_missing', 'login');
        }
      } else if (response.status === 401) {
        setError('Incorrect email or password. Please try again.');
        trackError('login_invalid_credentials', 'login');
      } else if (response.status === 404) {
        setError('No account found with this email. Please register first.');
        trackError('login_user_not_found', 'login');
      } else if (response.status === 403) {
        setError('Account access denied. Please verify your email or contact support.');
        trackError('login_access_denied', 'login');
      } else if (response.status === 400) {
        const msg: string = data.detail || data.message || '';
        setError(msg || 'Invalid login details. Please check your information.');
        trackError('login_validation_error', 'login');
      } else if (response.status === 422) {
        const detail = data.detail;
        const msg = Array.isArray(detail)
          ? detail.map((d: any) => d.msg).join(', ')
          : data.message || 'Invalid request. Please check your details.';
        setError(msg);
        trackError('login_validation_error', 'login');
      } else if (response.status === 429) {
        setError('Too many login attempts. Please wait a few minutes before trying again.');
        trackError('login_rate_limited', 'login');
      } else if (response.status >= 500) {
        setError('Server error. Please try again later.');
        trackError('login_server_error', 'login');
      } else {
        setError(data.detail || data.message || 'Login failed. Please try again.');
        trackError('login_unknown_error', 'login');
      }
    } catch (err: any) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        setError('Request timed out. Please check your connection and try again.');
        trackError('login_timeout', 'login');
      } else {
        setError('Connection failed. Please check your internet connection and try again.');
        trackError('login_network_error', 'login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <ForgotPassword
        onClose={() => setShowForgotPassword(false)}
        onBack={() => setShowForgotPassword(false)}
      />
    );
  }

  return (
    <div className="auth-container">
      <h2 className="auth-title">Welcome Back</h2>
      <form className="auth-form" onSubmit={handleLogin}>
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={error ? 'error' : ''}
            placeholder="Enter your email address"
            autoComplete="email"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="password">Password</label>
          <div className="password-input-container">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={error ? 'error' : ''}
              placeholder="Enter your password"
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <button
          type="submit"
          className="auth-button"
          disabled={isLoading}
        >
          {isLoading ? 'Logging in...' : 'Log In'}
        </button>
      </form>

      <div className="forgot-password-link">
        <button
          type="button"
          className="link-button"
          onClick={() => setShowForgotPassword(true)}
        >
          Forgot Password?
        </button>
      </div>

      <div className="auth-switch">
        Don't have an account?
        <Link to="/signup">Sign Up</Link>
      </div>
    </div>
  );
};

export default Login;
